'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, INLINES, Node, TopLevelBlock } from '@contentful/rich-text-types';
import renderEmbeddedEntry from '@/components/nodes/EmbeddedEntry';
import renderEntryHyperlink from '@/components/nodes/EntryHyperlink';
import renderEmbeddedAsset from '@/components/nodes/EmbeddedAsset';
import Link from '@/components/shared/Link/Link';
import RichTextCopy from '@/components/shared/RichTextCopy/RichTextCopy';
import type { DocumentWithEmbeddedEntries } from '@/libs/contentful/types';
import { useDisclosureNumberingMap } from '@/contexts/DisclosureProvider';

interface AgreementRichTextProps {
  body: DocumentWithEmbeddedEntries;
}

const AgreementRichText = ({ body }: AgreementRichTextProps) => {
  const disclosureNumberingMap = useDisclosureNumberingMap();

  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => <p className="my-4 last:mb-0">{children}</p>,
      [BLOCKS.HEADING_2]: (_node: Node, children: React.ReactNode) => (
        <h2 className="font-sans font-semibold text-heading-md my-8">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (_node: Node, children: React.ReactNode) => (
        <h3 className="font-sans font-semibold text-heading-sm my-4">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (_node: Node, children: React.ReactNode) => (
        <h4 className="font-sans font-semibold my-4">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (_node: Node, children: React.ReactNode) => (
        <h5 className="font-sans font-semibold my-4">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (_node: Node, children: React.ReactNode) => (
        <h6 className="font-sans font-semibold my-4">{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) => {
        const listStyleType = typeof node.data?.listStyleType === 'string' ? node.data.listStyleType : undefined;
        return (
          <ul className="list-disc pl-5 my-4 [&_ul]:pl-5" style={listStyleType ? { listStyleType } : undefined}>
            {children}
          </ul>
        );
      },
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => {
        const listStyleType = typeof node.data?.listStyleType === 'string' ? node.data.listStyleType : undefined;
        return (
          <ol className="list-decimal pl-5 my-4 [&_ol]:pl-5" style={listStyleType ? { listStyleType } : undefined}>
            {children}
          </ol>
        );
      },
      [BLOCKS.LIST_ITEM]: (_node: Node, children: React.ReactNode) => <li className="my-1">{children}</li>,
      [BLOCKS.TABLE]: (node: Node) => {
        const tableNode = node as Block;
        return (
          <div className="my-4 overflow-x-auto">
            <table className="w-full border-collapse border border-content-primary">
              <tbody>
                {tableNode.content.map((row, rowIdx) => (
                  <tr key={rowIdx} className="border border-content-primary">
                    {(row as Block).content.map((cell, cellIdx) => {
                      const cellBlock = cell as Block;
                      const Tag = cellBlock.nodeType === 'table-header-cell' ? 'th' : 'td';
                      return (
                        <Tag key={cellIdx} className="border border-content-primary p-2 text-left align-top">
                          <RichTextCopy
                            copy={{
                              json: {
                                nodeType: BLOCKS.DOCUMENT,
                                data: {},
                                content: cellBlock.content as TopLevelBlock[],
                              },
                            }}
                          />
                        </Tag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      },
      [BLOCKS.HR]: () => <hr className="my-4 border-content-primary" />,
      [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
        return <div className="my-4">{renderEmbeddedAsset(node, children, body)}</div>;
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => (
        <Link href={node.data.uri} className="inline underline">
          {children}
        </Link>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        return renderEntryHyperlink(node, children, body);
      },
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
        return renderEmbeddedEntry(node, children, body, disclosureNumberingMap);
      },
    },
  };

  if (!body.json) return null;

  return documentToReactComponents(body.json, options);
};

export { AgreementRichText };
